import { useState } from 'react';
import { PageHeader } from '@/components/shared/page';
import { DataTable, type Column } from '@/components/shared/data-table';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Avatar, Select } from '@/components/ui/misc';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useDebounce } from '@/hooks/use-debounce';
import { usePagination } from '@/hooks/use-pagination';
import { formatDateTime, timeAgo } from '@/lib/format';
import { titleCase } from '@/lib/utils';
import type { AuditLogRow } from '@/lib/types';
import { useAuditLog } from './api';

const ENTITY_TYPES = ['user', 'role', 'team', 'organization', 'file', 'sequence'];

/** Actions that deserve to stand out in a long list. */
const SEVERE = new Set([
  'deleted',
  'deactivated',
  'roles_changed',
  'impersonation_started',
  '2fa_disabled',
  'password_reset',
]);

export function AuditPage() {
  const pagination = usePagination();
  const [search, setSearch] = useState('');
  const [entityType, setEntityType] = useState('');
  const [inspecting, setInspecting] = useState<AuditLogRow | null>(null);

  const debounced = useDebounce(search);
  const { data, isLoading, error, refetch } = useAuditLog({
    page: pagination.page,
    limit: pagination.limit,
    q: debounced || undefined,
    entityType: entityType || undefined,
  });

  const columns: Column<AuditLogRow>[] = [
    {
      key: 'createdAt',
      header: 'When',
      className: 'whitespace-nowrap text-sm',
      cell: (row) => (
        <span title={formatDateTime(row.createdAt)}>{timeAgo(row.createdAt)}</span>
      ),
    },
    {
      key: 'actor',
      header: 'Who',
      cell: (row) =>
        row.actor ? (
          <div className="flex items-center gap-2">
            <Avatar name={row.actor.name} size={24} />
            <span className="truncate text-sm">{row.actor.name}</span>
          </div>
        ) : (
          <span className="text-sm text-muted-foreground">System</span>
        ),
    },
    {
      key: 'action',
      header: 'Action',
      cell: (row) => (
        <Badge variant={SEVERE.has(row.action) ? 'destructive' : 'secondary'}>
          {titleCase(row.action)}
        </Badge>
      ),
    },
    {
      key: 'entity',
      header: 'On',
      className: 'text-sm',
      cell: (row) => (
        <span>
          {titleCase(row.entityType)}
          {row.entityId && (
            <span className="ms-1 font-mono text-xs text-muted-foreground">
              {row.entityId.slice(-8)}
            </span>
          )}
        </span>
      ),
    },
    {
      key: 'ip',
      header: 'From',
      className: 'text-sm text-muted-foreground',
      cell: (row) => row.actorIp ?? '—',
    },
  ];

  return (
    <div>
      <PageHeader
        title="Audit log"
        description="Append-only record of every sensitive change. It cannot be edited or deleted from the application."
      />

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <Input
          value={search}
          onChange={(event) => {
            setSearch(event.target.value);
            pagination.reset();
          }}
          placeholder="Search action or entity…"
          className="h-9 w-full sm:max-w-xs"
        />
        <Select
          value={entityType}
          onChange={(event) => {
            setEntityType(event.target.value);
            pagination.reset();
          }}
          className="h-9 w-auto min-w-[10rem]"
        >
          <option value="">All record types</option>
          {ENTITY_TYPES.map((type) => (
            <option key={type} value={type}>
              {titleCase(type)}
            </option>
          ))}
        </Select>
      </div>

      <DataTable
        columns={columns}
        rows={data?.data ?? []}
        rowKey={(row) => row.id}
        isLoading={isLoading}
        error={error ? 'Could not load the audit log.' : null}
        onRetry={() => void refetch()}
        meta={data?.meta}
        onPageChange={pagination.setPage}
        onRowClick={(row) => setInspecting(row)}
        emptyTitle="Nothing logged yet"
        emptyDescription="Sensitive changes will appear here as they happen."
      />

      <Dialog open={Boolean(inspecting)} onOpenChange={(open) => !open && setInspecting(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {inspecting ? `${titleCase(inspecting.entityType)} — ${titleCase(inspecting.action)}` : ''}
            </DialogTitle>
          </DialogHeader>

          {inspecting && (
            <div className="space-y-4 text-sm">
              <dl className="grid grid-cols-2 gap-3">
                <Meta label="When" value={formatDateTime(inspecting.createdAt)} />
                <Meta label="Who" value={inspecting.actor?.name ?? 'System'} />
                <Meta label="IP address" value={inspecting.actorIp ?? '—'} />
                <Meta label="Request id" value={inspecting.requestId ?? '—'} />
                {inspecting.impersonatorId && (
                  <Meta label="Impersonated by" value={inspecting.impersonatorId} />
                )}
              </dl>

              <div className="grid gap-3 sm:grid-cols-2">
                <JsonBlock label="Before" value={inspecting.before} />
                <JsonBlock label="After" value={inspecting.after} />
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Meta({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="truncate font-mono text-xs">{value}</dd>
    </div>
  );
}

function JsonBlock({ label, value }: { label: string; value: unknown }) {
  return (
    <div>
      <div className="mb-1 text-xs font-medium text-muted-foreground">{label}</div>
      <pre className="scrollbar-thin max-h-64 overflow-auto rounded-md border bg-muted/40 p-2 text-[11px] leading-relaxed">
        {value === null || value === undefined ? '—' : JSON.stringify(value, null, 2)}
      </pre>
    </div>
  );
}
